/**
 * Yahtzee Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats including high scores and Yahtzees rolled.
 *
 * Follows LudoStatisticsService pattern.
 */

import {
  YahtzeeStatistics,
  YahtzeeGameResult,
  YahtzeeGameStats,
  YahtzeeDifficulty,
  YahtzeeGameSettings,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class YahtzeeStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'yahtzee_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): YahtzeeStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): YahtzeeGameStats {
    return {
      wins: 0,
      losses: 0,
      winPercentage: 0,
      averageScore: 0,
      highScore: 0,
      totalYahtzees: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): YahtzeeStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as YahtzeeStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get yahtzee statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: YahtzeeGameResult,
    playerScore: number,
    isWinner: boolean,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], playerScore, isWinner, result);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, playerScore, isWinner, result);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, playerScore, isWinner, result);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record yahtzee game result:', error);
      return false;
    }
  }

  private updateGameStats(
    gameStats: YahtzeeGameStats,
    playerScore: number,
    isWinner: boolean,
    result: YahtzeeGameResult
  ): void {
    if (isWinner) {
      gameStats.wins++;
    } else {
      gameStats.losses++;
    }

    const totalGames = gameStats.wins + gameStats.losses;
    gameStats.winPercentage = totalGames > 0 ? (gameStats.wins / totalGames) * 100 : 0;

    // Update average score
    const prevTotal = gameStats.averageScore * (totalGames - 1);
    gameStats.averageScore = totalGames > 0 ? (prevTotal + playerScore) / totalGames : playerScore;

    // Update high score
    if (playerScore > gameStats.highScore) {
      gameStats.highScore = playerScore;
    }

    // Track Yahtzees rolled
    gameStats.totalYahtzees += result.yahtzeesRolled;
  }

  /** Clear all statistics. */
  clearStatistics(userId?: string): void {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear yahtzee statistics:', error);
    }
  }
}
